import type { EvalRunData } from "@global/types/ai-evals";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useMemo } from "react";
import { groupsOf, pct, type Row, type RunKey, rowsOf, score } from "./scoring";
import { Delta, VerdictChip, VerdictMix } from "./ui";

export type CheckFilters = { scope: "all" | "interview" | "case"; group: string; verdict: string; q: string };

interface ChecksProps {
  data: EvalRunData;
  baseline: EvalRunData | null;
  filters: CheckFilters;
  onFilters: (filters: CheckFilters) => void;
  onOpenCheck: (id: string) => void;
}

export const Checks: React.FC<ChecksProps> = ({ data, baseline, filters, onFilters, onOpenCheck }) => {
  const now = useMemo(() => rowsOf(data), [data]);
  const before = useMemo(() => rowsOf(baseline), [baseline]);
  const visible = data.checks.filter((c) => {
    const rows = now.filter((r) => r.id === c.id);
    if (filters.scope !== "all" && c.scope !== filters.scope) return false;
    if (filters.group !== "all" && c.group !== filters.group) return false;
    if (filters.verdict !== "all" && !rows.some((r) => r.verdict === filters.verdict)) return false;
    if (filters.q && !`${c.id} ${c.text}`.toLowerCase().includes(filters.q.toLowerCase())) return false;
    return true;
  });
  const set = (patch: Partial<CheckFilters>) => onFilters({ ...filters, ...patch });

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Scope</InputLabel>
          <Select label="Scope" value={filters.scope} onChange={(e) => set({ scope: e.target.value as CheckFilters["scope"] })}>
            <MenuItem value="all">Interview and case</MenuItem>
            <MenuItem value="interview">Interview</MenuItem>
            <MenuItem value="case">Case</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>Group</InputLabel>
          <Select label="Group" value={filters.group} onChange={(e) => set({ group: e.target.value })}>
            <MenuItem value="all">All groups</MenuItem>
            {groupsOf(data).map(([group, title]) => (
              <MenuItem key={group} value={group}>
                {group} {title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Verdict</InputLabel>
          <Select label="Verdict" value={filters.verdict} onChange={(e) => set({ verdict: e.target.value })}>
            <MenuItem value="all">Any verdict</MenuItem>
            <MenuItem value="fail">Has a fail</MenuItem>
            <MenuItem value="concerns">Has concerns</MenuItem>
            <MenuItem value="pass">Has a pass</MenuItem>
          </Select>
        </FormControl>
        <TextField size="small" label="Search checks" value={filters.q} onChange={(e) => set({ q: e.target.value })} sx={{ minWidth: 240 }} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {visible.length} of {data.checks.length} checks. Click a check for every verdict and its evidence.
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Check</TableCell>
              <TableCell>Verdicts</TableCell>
              <TableCell align="right">✓</TableCell>
              <TableCell align="right">!</TableCell>
              <TableCell align="right">✕</TableCell>
              <TableCell align="right">–</TableCell>
              <TableCell align="right">Score</TableCell>
              {baseline && <TableCell align="right">Baseline</TableCell>}
              {baseline && <TableCell align="right">Δ</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((c) => {
              const rows = now.filter((r) => r.id === c.id);
              const count = (v: string) => rows.filter((r) => r.verdict === v).length;
              const s = score(rows);
              const b = baseline ? score(before.filter((r) => r.id === c.id)) : null;
              return (
                <TableRow key={c.id} hover sx={{ cursor: "pointer" }} onClick={() => onOpenCheck(c.id)}>
                  <TableCell>
                    <b>{c.id}</b> {c.text}
                  </TableCell>
                  <TableCell sx={{ width: 140 }}>
                    <VerdictMix rows={rows} />
                  </TableCell>
                  <TableCell align="right">{count("pass")}</TableCell>
                  <TableCell align="right">{count("concerns")}</TableCell>
                  <TableCell align="right">{count("fail")}</TableCell>
                  <TableCell align="right" sx={{ color: "text.disabled" }}>
                    {count("na")}
                  </TableCell>
                  <TableCell align="right">{pct(s)}</TableCell>
                  {baseline && <TableCell align="right">{pct(b)}</TableCell>}
                  {baseline && (
                    <TableCell align="right">
                      <Delta now={s} before={b} />
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const VerdictTable: React.FC<{ rows: Row[]; onOpenRun?: (key: RunKey) => void }> = ({ rows, onOpenRun }) =>
  rows.length === 0 ? (
    <Typography color="text.secondary">No results.</Typography>
  ) : (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Run</TableCell>
            <TableCell>Verdict</TableCell>
            <TableCell>Votes</TableCell>
            <TableCell>Evidence</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={`${r.scenarioId}-${r.run}`} hover={Boolean(onOpenRun)} sx={{ cursor: onOpenRun ? "pointer" : "default" }} onClick={() => onOpenRun?.(r)}>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                {r.scenarioId} #{r.run}
              </TableCell>
              <TableCell>
                <VerdictChip verdict={r.verdict} />
              </TableCell>
              <TableCell sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>{r.votes ? r.votes.join(" · ") : r.by}</TableCell>
              <TableCell sx={{ color: "text.secondary" }}>{r.evidence}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

interface CheckDetailProps {
  id: string;
  data: EvalRunData;
  baseline: EvalRunData | null;
  onBack: () => void;
  onOpenRun: (key: RunKey) => void;
}

export const CheckDetail: React.FC<CheckDetailProps> = ({ id, data, baseline, onBack, onOpenRun }) => {
  const check = data.checks.find((c) => c.id === id);
  const now = rowsOf(data).filter((r) => r.id === id);
  const before = rowsOf(baseline).filter((r) => r.id === id);
  if (!check) return <Typography>Unknown check {id}.</Typography>;
  const criteria: [React.ReactNode, string][] = [
    ["Applies", check.applies],
    ["Method", check.method],
    [<VerdictChip key="p" verdict="pass" />, check.pass],
    [<VerdictChip key="c" verdict="concerns" />, check.concerns],
    [<VerdictChip key="f" verdict="fail" />, check.fail],
  ];
  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 1 }}>
        Checks
      </Button>
      <Typography variant="overline" color="text.secondary" display="block">
        {check.group} {check.groupTitle} · {check.scope}
      </Typography>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {check.id} · {check.text}
      </Typography>
      <Paper variant="outlined" sx={{ p: 2, display: "grid", gridTemplateColumns: "max-content 1fr", gap: "6px 16px", alignItems: "center" }}>
        {criteria.map(([label, text], i) => (
          <React.Fragment key={i}>
            <Typography variant="body2" color="text.secondary" component="div">
              {label}
            </Typography>
            <Typography variant="body2">{text}</Typography>
          </React.Fragment>
        ))}
      </Paper>
      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        This run · score {pct(score(now))}
      </Typography>
      <VerdictTable rows={now} onOpenRun={onOpenRun} />
      {baseline && (
        <>
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            Baseline · score {pct(score(before))}
          </Typography>
          <VerdictTable rows={before} />
        </>
      )}
    </Box>
  );
};
